// apps/web/src/components/CardBackgroundSelector.js
import React, { useState, useRef } from 'react';

const GRADIENT_PRESETS = [
  { id: 'professional-grey', name: 'Grey', value: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)', free: true },
  { id: 'ocean-blue', name: 'Ocean', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', free: true },
  { id: 'sunset', name: 'Sunset', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', free: true },
  { id: 'forest', name: 'Forest', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', free: true }
];

export default function CardBackgroundSelector({ initialSettings = {}, onChange }) {
  const [backgroundType, setBackgroundType] = useState(initialSettings.type || 'none');
  const [solidColor, setSolidColor] = useState(initialSettings.solid?.color || '');
  const [gradientPreset, setGradientPreset] = useState(initialSettings.gradient?.preset || '');
  const [gradientCustomStart, setGradientCustomStart] = useState(initialSettings.gradient?.custom?.startColor || '#667eea');
  const [gradientCustomEnd, setGradientCustomEnd] = useState(initialSettings.gradient?.custom?.endColor || '#764ba2');
  const [gradientDirection, setGradientDirection] = useState(initialSettings.gradient?.custom?.direction || '135deg');
  const [imageBase64, setImageBase64] = useState(initialSettings.image?.base64 || '');
  const [imageBlur, setImageBlur] = useState(initialSettings.image?.blur || 0);
  const [imageOverlay, setImageOverlay] = useState(initialSettings.image?.overlay || 0.3);
  const [imagePreview, setImagePreview] = useState(initialSettings.image?.base64 || '');
  const [message, setMessage] = useState('');
  const fileInputRef = useRef(null);

  // Notify parent of changes
  const notifyChange = (updates) => {
    const settings = {
      type: updates.backgroundType !== undefined ? updates.backgroundType : backgroundType,
      solid: { color: updates.solidColor !== undefined ? updates.solidColor : solidColor },
      gradient: {
        preset: updates.gradientPreset !== undefined ? updates.gradientPreset : gradientPreset,
        custom: {
          startColor: updates.gradientCustomStart !== undefined ? updates.gradientCustomStart : gradientCustomStart,
          endColor: updates.gradientCustomEnd !== undefined ? updates.gradientCustomEnd : gradientCustomEnd,
          direction: updates.gradientDirection !== undefined ? updates.gradientDirection : gradientDirection
        }
      },
      image: {
        base64: updates.imageBase64 !== undefined ? updates.imageBase64 : imageBase64,
        blur: updates.imageBlur !== undefined ? updates.imageBlur : imageBlur,
        overlay: updates.imageOverlay !== undefined ? updates.imageOverlay : imageOverlay
      }
    };
    onChange?.(settings);
  };

  const handleTypeChange = (newType) => {
    setBackgroundType(newType);
    notifyChange({ backgroundType: newType });
  };

  const handleSolidColorChange = (color) => {
    setSolidColor(color);
    notifyChange({ solidColor: color });
  };

  const handleGradientPresetChange = (preset) => {
    setGradientPreset(preset);
    notifyChange({ gradientPreset: preset });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // SECURITY: Generous size limit to prevent DoS, but images will be compressed
    if (file.size > 5 * 1024 * 1024) { // 5MB limit (pre-compression)
      setMessage('✗ Image must be under 5MB (will be automatically optimized)');
      return;
    }

    try {
      // SECURITY: Validate actual file type using magic numbers (file signatures)
      const isValidImage = await validateImageFile(file);
      if (!isValidImage) {
        setMessage('✗ Invalid image file. Only JPEG, PNG, and WebP are allowed.');
        return;
      }

      const compressed = await compressImage(file);
      setImageBase64(compressed);
      setImagePreview(compressed);
      notifyChange({ imageBase64: compressed });
      setMessage('✓ Image uploaded and optimized');
    } catch (err) {
      console.error('[CardBackgroundSelector] Image compression failed:', err);
      setMessage('✗ Failed to process image');
    }
  };

  // SECURITY: Validate image using magic numbers (file signatures)
  // This prevents malicious files disguised as images
  async function validateImageFile(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = (e) => {
        if (!e.target?.result) {
          resolve(false);
          return;
        }

        const arr = new Uint8Array(e.target.result).subarray(0, 12);
        let header = '';
        for (let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16).padStart(2, '0');
        }

        // Check magic numbers for safe image formats
        // SECURITY: SVG is BLOCKED because it can contain JavaScript
        const validSignatures = {
          'ffd8ff': 'image/jpeg',           // JPEG
          '89504e47': 'image/png',          // PNG
          '52494646': 'image/webp',         // WebP (starts with RIFF)
        };

        const isValid = Object.keys(validSignatures).some(sig => header.startsWith(sig));
        resolve(isValid);
      };
      reader.onerror = () => resolve(false);
      // Only read first 12 bytes for signature check
      reader.readAsArrayBuffer(file.slice(0, 12));
    });
  }

  // Aggressive compression for cards (800px max, 60% quality)
  function compressImage(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          let width = img.width;
          let height = img.height;
          const maxWidth = 800;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressed = canvas.toDataURL('image/jpeg', 0.6);
          resolve(compressed);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const getCurrentGradient = () => {
    if (gradientPreset) {
      const preset = GRADIENT_PRESETS.find(p => p.id === gradientPreset);
      return preset?.value || '';
    }
    return `linear-gradient(${gradientDirection}, ${gradientCustomStart} 0%, ${gradientCustomEnd} 100%)`;
  };

  return (
    <div style={{ marginTop: 12 }}>
      <label className="TaskEditLabel">Card Background</label>

      {/* Type Selection */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`Btn small ${backgroundType === 'none' ? 'primary' : ''}`}
          onClick={() => handleTypeChange('none')}
        >
          None
        </button>
        <button
          type="button"
          className={`Btn small ${backgroundType === 'solid' ? 'primary' : ''}`}
          onClick={() => handleTypeChange('solid')}
        >
          Solid
        </button>
        <button
          type="button"
          className={`Btn small ${backgroundType === 'gradient' ? 'primary' : ''}`}
          onClick={() => handleTypeChange('gradient')}
        >
          Gradient
        </button>
        <button
          type="button"
          className={`Btn small ${backgroundType === 'image' ? 'primary' : ''}`}
          onClick={() => handleTypeChange('image')}
        >
          Image
        </button>
      </div>

      {/* Solid Color */}
      {backgroundType === 'solid' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input
            type="color"
            value={solidColor || '#3b82f6'}
            onChange={(e) => handleSolidColorChange(e.target.value)}
            style={{ width: 40, height: 32, border: '1px solid var(--border-light)', borderRadius: 4, cursor: 'pointer' }}
          />
          <input
            type="text"
            className="TaskEditInput"
            value={solidColor}
            onChange={(e) => handleSolidColorChange(e.target.value)}
            placeholder="#3b82f6"
            style={{ flex: 1, fontSize: 13 }}
          />
        </div>
      )}

      {/* Gradient */}
      {backgroundType === 'gradient' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginBottom: 12 }}>
            {GRADIENT_PRESETS.map(preset => (
              <div
                key={preset.id}
                onClick={() => handleGradientPresetChange(preset.id)}
                style={{
                  height: 50,
                  background: preset.value,
                  borderRadius: 6,
                  border: gradientPreset === preset.id ? '2px solid var(--accent-primary)' : '1px solid var(--border-light)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-end',
                  padding: 4,
                  fontSize: 10,
                  color: '#fff',
                  fontWeight: 600,
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                }}
              >
                {preset.name}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="Btn small"
            onClick={() => handleGradientPresetChange('')}
            style={{ fontSize: 12, marginBottom: 12 }}
          >
            Use Custom
          </button>

          {!gradientPreset && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="color"
                value={gradientCustomStart}
                onChange={(e) => { setGradientCustomStart(e.target.value); notifyChange({ gradientCustomStart: e.target.value }); }}
                style={{ width: 40, height: 32, border: '1px solid var(--border-light)', borderRadius: 4, cursor: 'pointer' }}
              />
              <input
                type="color"
                value={gradientCustomEnd}
                onChange={(e) => { setGradientCustomEnd(e.target.value); notifyChange({ gradientCustomEnd: e.target.value }); }}
                style={{ width: 40, height: 32, border: '1px solid var(--border-light)', borderRadius: 4, cursor: 'pointer' }}
              />
            </div>
          )}

          {/* Preview */}
          <div style={{ height: 60, background: getCurrentGradient(), borderRadius: 6, border: '1px solid var(--border-light)' }} />
        </>
      )}

      {/* Image */}
      {backgroundType === 'image' && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="Btn small"
            onClick={() => fileInputRef.current?.click()}
            style={{ marginBottom: 12 }}
          >
            📁 Choose Image (Max 5MB)
          </button>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 12 }}>
            JPG, PNG, WebP • Automatically optimized
          </div>

          {imagePreview && (
            <>
              <div style={{
                height: 80,
                backgroundImage: `url(${imagePreview})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: 6,
                border: '1px solid var(--border-light)',
                position: 'relative',
                filter: `blur(${imageBlur}px)`,
                marginBottom: 12
              }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: `rgba(0, 0, 0, ${imageOverlay})`,
                  borderRadius: 6
                }} />
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                  Blur: {imageBlur}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="5"
                  step="1"
                  value={imageBlur}
                  onChange={(e) => { setImageBlur(Number(e.target.value)); notifyChange({ imageBlur: Number(e.target.value) }); }}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                  Overlay: {Math.round(imageOverlay * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="0.8"
                  step="0.1"
                  value={imageOverlay}
                  onChange={(e) => { setImageOverlay(Number(e.target.value)); notifyChange({ imageOverlay: Number(e.target.value) }); }}
                  style={{ width: '100%' }}
                />
              </div>

              <button
                type="button"
                className="Btn small danger"
                onClick={() => {
                  setImageBase64('');
                  setImagePreview('');
                  notifyChange({ imageBase64: '' });
                }}
              >
                Remove Image
              </button>
            </>
          )}
        </>
      )}

      {message && (
        <div style={{
          padding: 8,
          borderRadius: 4,
          backgroundColor: message.includes('✓') ? '#d1fae5' : '#fee2e2',
          color: message.includes('✓') ? '#065f46' : '#991b1b',
          fontSize: 12,
          marginTop: 8
        }}>
          {message}
        </div>
      )}
    </div>
  );
}
